import { AfterViewInit, Component, ElementRef, HostListener, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';

@Component({
  selector: 'app-horizontal-scroll',
  templateUrl: './horizontal-scroll.component.html',
  styleUrls: ['./horizontal-scroll.component.scss']
})
export class HorizontalScrollComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() reversed: boolean;
  @Input() roundButtons: boolean = true;
  /** 'page' = scroll by full container width; 'item' = scroll by first item width (for trailers, child-movies, etc.) */
  @Input() scrollMode: 'page' | 'item' = 'page';
  @ViewChild('cmContainer', {static: false}) cmContainer: ElementRef;

  showArrowRight: boolean = false;
  showArrowLeft: boolean = false;

  resizeTimeout;

  scrollTimeout;

  private destroyed = false;

  isEdge = /Edge/.test(navigator.userAgent);
  isSafari = /apple/i.test(navigator.vendor);

  constructor() { }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event){
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.updateArrows();
    }, 50);
  }

  ngAfterViewInit(): void {
    if (!this.cmContainer || !this.cmContainer.nativeElement) {
      return;
    }
    const el = this.cmContainer.nativeElement;

    if(this.reversed) {
      if(!this.isEdge && el.scrollTo) {
        el.scrollTo(el.scrollWidth, 0);
      }
      else {
        el.scrollLeft = el.scrollWidth;
      }
    }

    el.addEventListener('scroll', () => {
      if (this.destroyed) { return; }
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.updateArrows();
      }, 50);
    });

    setTimeout(() => {
      this.updateArrows();
    }, 500);

  }

  ngOnInit() {

  }

  updateArrows() {
    if (this.destroyed || !this.cmContainer || !this.cmContainer.nativeElement) {
      return;
    }
    const el = this.cmContainer.nativeElement;
    this.showArrowLeft = el.scrollLeft > 0;
    this.showArrowRight = el.scrollWidth > el.clientWidth &&
      el.scrollWidth > el.clientWidth + Math.ceil(el.scrollLeft);
  }

  previous() {
    if (!this.cmContainer || !this.cmContainer.nativeElement) {
      return;
    }
    if (this.scrollMode === 'item' && this.scrollToPreviousItem()) {
      return;
    }
    const scrollAmount = this.getOptimalScrollAmount();
    let moveLeft = -scrollAmount;

    if (this.cmContainer.nativeElement.scrollLeft - scrollAmount < 0) {
      moveLeft = -this.cmContainer.nativeElement.scrollLeft;
    }

    this.scrollBy(moveLeft, 0);
  }

  next() {
    if (!this.cmContainer || !this.cmContainer.nativeElement) {
      return;
    }
    if (this.scrollMode === 'item' && this.scrollToNextItem()) {
      return;
    }
    const scrollAmount = this.getOptimalScrollAmount();
    let moveLeft = scrollAmount;

    const remainingScroll = this.cmContainer.nativeElement.scrollWidth -
      (this.cmContainer.nativeElement.scrollLeft + this.cmContainer.nativeElement.clientWidth);

    if (remainingScroll < scrollAmount) {
      moveLeft = remainingScroll;
    }

    this.scrollBy(moveLeft, 0);
  }

  getOptimalScrollAmount(): number {
    if (!this.cmContainer || !this.cmContainer.nativeElement) {
      return 0;
    }
    const container = this.cmContainer.nativeElement;
    if (this.scrollMode === 'item') {
      const itemWidth = this.getFirstItemWidth(container);
      if (itemWidth > 0) {
        return itemWidth;
      }
    }
    // Page-wise scrolling: scroll by the visible container width
    return container.clientWidth;
  }

  private getFirstItemWidth(container: HTMLElement): number {
    const content = container.firstElementChild;
    const firstItem = content && content.firstElementChild as HTMLElement;
    if (!firstItem) {
      return 0;
    }
    const nextItem = firstItem.nextElementSibling as HTMLElement;
    if (nextItem) {
      return nextItem.offsetLeft - firstItem.offsetLeft;
    }
    const style = window.getComputedStyle(firstItem);
    const marginRight = parseFloat(style.marginRight) || 0;
    return firstItem.offsetWidth + marginRight;
  }

  /** Returns true if scroll was performed. Scrolls so the next item's left edge aligns with container. */
  private scrollToNextItem(): boolean {
    if (!this.cmContainer || !this.cmContainer.nativeElement) {
      return false;
    }
    const container = this.cmContainer.nativeElement as HTMLElement;
    const content = container.firstElementChild as HTMLElement;
    if (!content || content.children.length < 2) {
      return false;
    }
    const contentRect = content.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    for (let i = 0; i < content.children.length; i++) {
      const item = content.children[i] as HTMLElement;
      const itemLeftInContent = item.getBoundingClientRect().left - contentRect.left;
      if (itemLeftInContent > scrollLeft + 5) {
        this.scrollTo(itemLeftInContent);
        return true;
      }
    }
    return false;
  }

  /** Returns true if scroll was performed. Scrolls so the previous item's left edge aligns with container. */
  private scrollToPreviousItem(): boolean {
    if (!this.cmContainer || !this.cmContainer.nativeElement) {
      return false;
    }
    const container = this.cmContainer.nativeElement as HTMLElement;
    const content = container.firstElementChild as HTMLElement;
    if (!content || content.children.length < 2) {
      return false;
    }
    const contentRect = content.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    for (let i = content.children.length - 1; i >= 0; i--) {
      const item = content.children[i] as HTMLElement;
      const itemLeftInContent = item.getBoundingClientRect().left - contentRect.left;
      if (itemLeftInContent < scrollLeft - 5) {
        this.scrollTo(itemLeftInContent);
        return true;
      }
    }
    this.scrollTo(0);
    return true;
  }

  private scrollTo(targetScrollLeft: number) {
    if (!this.cmContainer || !this.cmContainer.nativeElement) {
      return;
    }
    const container = this.cmContainer.nativeElement;
    if (!this.isEdge && !this.isSafari && container.scrollTo) {
      container.scrollTo({
        top: 0,
        left: targetScrollLeft,
        behavior: 'smooth'
      });
    } else {
      container.scrollLeft = targetScrollLeft;
      this.updateArrows();
    }
  }

  scrollBy(xValue: number, yValue: number) {
    if (!this.cmContainer || !this.cmContainer.nativeElement) {
      return;
    }
    if(!this.isEdge && !this.isSafari && this.cmContainer.nativeElement.scrollBy) {
      this.cmContainer.nativeElement.scrollBy({
        top: yValue,
        left: xValue,
        behavior: 'smooth'
      });
    }
    else {
      this.cmContainer.nativeElement.scrollLeft = this.cmContainer.nativeElement.scrollLeft + xValue;
      this.cmContainer.nativeElement.scrollTop = this.cmContainer.nativeElement.scrollTop + yValue;
    }
  }

}
